import {useQuery} from '@tanstack/react-query'
import {TransactionListSchemaResponse, ActivityListSchemaResponse} from '../domain'
import {toast} from 'react-toastify'
import useRequestParams from 'src/feature/bite/hook/request'
import {useTransactionList, useActivityList} from '../hook'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import {useAuthUser} from 'react-auth-kit'

export function useGetTransactionList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listFilter, listSet} = useTransactionList()

  const userID = auth()?.id
  const merchantID = auth()?.merchantID

  return useQuery(
    ['AllTransactionList', listFilter],
    async () => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Transaction/ListByStore`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          storeID: merchantID,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = TransactionListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = TransactionListSchemaResponse.parse(Data)

      listSet(parse.response)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetActivityList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listFilter, listSet} = useActivityList()

  const userID = auth()?.id
  const merchantID = auth()?.merchantID

  return useQuery(
    ['AllActivityList', listFilter],
    async () => {
      const Response = await fetch(`${API}/Member/GetMemberPackageActivities`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          storeID: merchantID,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ActivityListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ActivityListSchemaResponse.parse(Data)

      listSet(parse.response)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useRenewActivities() {
  const {headers, signOut} = useRequestParams()
  const {listFilterSet, listSet, listFilter} = useActivityList()
  const auth = useAuthUser()
  const userID = auth()?.id
  const merchantID = auth()?.merchantID

  const timeZoneOffSet = getTimeZone()

  const {data, isLoading, error, refetch} = useQuery(
    ['renewActivities'],
    async () => {
      listFilter.filterList = listFilter.filterList.filter((item) => item.value?.[0] !== '')

      const Response = await fetch(`${API}/Member/GetMemberPackageActivities`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          storeID: merchantID,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ActivityListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ActivityListSchemaResponse.parse(Data)

      listSet(parse.response)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error('Error fetching transactions:', error) // Informative error handling
      },
      // Refetch data automatically when the 'handleSubmit' function is called
      refetchOnWindowFocus: false, // Avoid unnecessary refetches (optional)
      refetchInterval: 0, // Disable automatic refetching (optional)
    }
  )

  // Call refetch within 'handleSubmit' to manually trigger data refresh
  const handleRefresh = () => {
    refetch()
  }

  return {data, isLoading, error, handleRefresh}
}
